import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { BLACKBODY_CHUNK } from './ember.js';

// Shared-rig contract: forbidden. There is one light here, the ray from the
// window, and the smoke exists only where that ray crosses it. A wash from
// anywhere else would put the smoke everywhere and the piece would be gone.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'incense',
  family: 'organic',
  danceability: 0.10,
  energyLevel: 0.10,
  bpmMin: 40,
  bpmMax: 110,
  movement: 0.15,
  tonality: 0.95,
  prefers: { dynamics: [0.30, 1.0], percussiveness: [0.0, 0.30] },
  enabled: true
};

// Incense: a stick burning in macro, in a ray of light, and its smoke.
//
// THE STICK IS A PHOTOGRAPH. tools/build-scenes.mjs ships incense-scene.jpg
// (an unlit stick in its ceramic holder on a wooden table) with three
// measured points: where the paste begins (tip), where the stick enters the
// holder (base), and the stick's angle. Everything procedural - the ember,
// the ash, the smoke, the ray, the dust - is placed from those three numbers,
// and `?incensetip=1` draws them over the photo so a wrong measurement shows
// at once. The photo itself is held DARK (luminance capped at 0.35): the
// scene is guessed at, and the light in the picture comes from the ember and
// from the ray, both of which light the photo locally. Without the photo the
// procedural stick of the first version stands in, with a warning.
//
// THE PHENOMENON IS THE TRANSITION. A thread of smoke rises perfectly
// laminar - a ribbon a few pixels wide, almost straight - until, at one
// height, it breaks into turbulence. In the world that height is set by the
// plume's speed and the stillness of the air. Here it is set by the track's
// energy: calm passage, tall straight column; dense passage, it breaks early
// and the top of the frame is all whorls. That is the one thing the music
// does to the picture, and it is read without being an event.
//
// THE BREAK IS AN INSTABILITY, NOT A CUT. Over the top 20% of the laminar
// column the ribbon starts to oscillate, splits in two strands, the amplitude
// grows and the thread fades out into the whorls, which are born at the tear.
//
// THE SMOKE IS ONLY VISIBLE IN THE RAY. Lighthouse's principle - everything
// is revealed by one cone function - with a single fixed oblique source, set
// to cross the smoke's path between 30% and 60% of its height. Ribbon,
// whorls and dust all multiply their alpha by the same revealAt(), and keep
// a tenth of it outside.
//
// NOTHING IS FAST. The ribbon sways with a three-second period, a whorl
// lives eight seconds, the ash falls in half a second and that happens once.

// --- the frame -----------------------------------------------------------------
// Everything lives on the plane z = 0, in world units: the frame's half
// height is HH at the camera's distance, x spans +-HH*aspect. The photo is
// fitted BY HEIGHT (image height = frame height, centred): on the kiosk's
// 3.2:1 screen its sides are the clamped edge columns of a dark wall and a
// table, which is fine; cropping by width there would have lost the holder.
const CAMERA_Z = 10.0;
const SCENE_MANIFEST = 'textures/incense-scene.json';
const SCENE_ASPECT = 16 / 9;
// The fallback's own measurements, the same three numbers, in image space.
const FALLBACK_META = {
  tip: { x: 0.433, y: 0.310 }, angle: 24.5, base: { x: 0.311, y: 0.785 }, sourceW: 1365,
  // the same stick geometry as build-scenes.mjs writes into the manifest
  stick: { widthPx: 21, maskHalfPx: 13.5, maskOffsetPx: -5.5, extremity: { x: 0.446, y: 0.258 } }
};
const STICK_PX = 11;             // the photographed stick's dark paste width, in 720p pixels (the ash's width)
const HOLDER_W = 0.13, HOLDER_H = 0.045;

// --- the photo ------------------------------------------------------------------
const PHOTO_CAP = 0.35;          // luminance ceiling: the scene is guessed at
const PHOTO_IN_RAY = 0.60;       // where the ray passes
const EMBER_GLOW_PX = 60;        // the ember's warm radial light on the photo

// --- the smoke -------------------------------------------------------------------
const RIBBON_SAMPLES = 200;
const TWIN_SAMPLES = 80;
const RIBBON_PX = 4.0;           // 2-5 px
const RIBBON_ALPHA = 0.35;
const SWAY_PERIOD = 3.0;
const SWAY_PX = 4.0;
const SWAY_MID_PX = 4.0;         // bands.mid adds up to this much
const INSTABILITY = 0.20;        // the top 20% of the column
const RISE_TIME = 4.0;           // seconds for the thread to climb to the top
const AXIS_RUN = 0.15;           // of the thread's height: it leaves along the stick and is vertical by here
const TRANSITION_MAX = 0.75, TRANSITION_SPAN = 0.55;   // h = 0.75 - 0.55 * energy
const ENERGY_TAU = 4.0;          // seconds: the energy is read through a slow filter
const ENERGY_DEFAULT = 0.35;
const WHORL_RATE = 6;            // per second
const WHORL_LIFE = 8.0;
const WHORL_RISE = 0.040;        // frame heights per second: 0.32 H in a life, the cloud stands 25% above the break
const WHORL_SIZE = [0.012, 0.016]; // half size at birth, of the frame height: ~20 px across at 720p
const WHORL_GROW = 4.5;          // size at death, relative: ~90 px across
const WHORL_ALPHA = 0.30;
const WHORL_OUTSIDE = 0.35;      // the cloud above the ray keeps this much (the thread keeps RAY_OUTSIDE)
const CURL_SCALE = 0.55;         // of the frame height
const CURL_SPEED = 0.10;         // frame heights per second, at most
const SMOKE_COLOR = [0.90, 0.93, 1.0];

// --- the light --------------------------------------------------------------------
const RAY_CROSS = [0.30, 0.60];         // the ray crosses the smoke's path between these heights
const RAY_ANGLE_DEG = 45;               // from the horizontal, coming down from the right
const RAY_SOFT = 0.55;                  // of the half width, the soft rim
const RAY_GAIN = 0.022;                 // the visible beam's own glow
const RAY_OUTSIDE = 0.10;               // what the smoke keeps outside the ray
const DUST_COUNT = 300;
const DUST_DRIFT = 0.02;

// --- the ember and the ash ------------------------------------------------------
const EMBER_R = 0.012;
const EMBER_FLARE_EVERY = [8, 15];
const EMBER_FLARE_S = 0.9;
// The ember covers the WHOLE stick over the track: now that the stick really
// shortens, half the track is half the stick, and the brief's check reads
// exactly that (at progress 0.5, the stick visible on its lower half). At
// 0.60 it sat at 30% and the check was off by construction.
const BURN_SHARE = 1.0;
const ASH_FALL_S = 0.5;
const ASH_PUFF = 5;              // whorls released when the ash falls
// THE ASH IS SHORT, AND IT FALLS. Above the ember there is nothing of the
// photograph any more - the wall behind the stick is painted back in at build
// time (build-scenes.mjs) and the stick is only drawn from the holder up to
// the ember - so the stick visibly shortens as the track plays, and what sits
// above the ember is only this: a stub of at most 5% of the stick, matte grey,
// bent a little toward the ground, which grows slowly and drops when it gets
// there, every 40 to 70 s on its own clock. Its growth is TIMED rather than
// tied to the ember's recession: the ember covers 60% of the stick over a
// track, which on a four-minute song would drop a 5% ash every 20 s.
const ASH_MAX = 0.05;            // of the stick's length
const ASH_PERIOD = [40, 70];     // seconds from one fall to the next
// It grows FAST to 3% and then creeps to 5%: the brief wants a short ash "of
// 3 to 5%" that also starts again from nothing after a fall. Linear growth
// from zero would spend most of its life under 3% - a speck, not an ash.
// With this curve it is between 3 and 5% for 85% of its life.
const ASH_QUICK = 0.15;          // of the period: by here it is at ASH_FIRST
const ASH_FIRST = 0.03;
const ASH_SAG = 0.08;            // of the ash's length, the bend toward the ground at its far end
const ASH_GREY = 0.45;
const ASH_EMBER_PX = 4;          // the red-to-grey gradient at the ember
const ASH_SEGMENTS = 12;
const MASK_FEATHER_PX = 1;       // the stick's cut at the ember

const SAFE_FLOOR = 0.6;
const TIER = {
  0: { whorls: 60, dust: 0, photo: 1600 },
  1: { whorls: 120, dust: DUST_COUNT, photo: 1600 },
  2: { whorls: 200, dust: DUST_COUNT, photo: 2560 }
};
const DEFAULT_TIER = 2;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function rand(a, b) { return a + Math.random() * (b - a); }

// A small value-noise field on the CPU, for the curl that drifts the
// whorls sideways. Bounded inputs, a few hundred evaluations a frame.
function hash2(x, y) {
  const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return s - Math.floor(s);
}
function vnoise(x, y) {
  const ix = Math.floor(x), iy = Math.floor(y);
  let fx = x - ix, fy = y - iy;
  fx = fx * fx * (3 - 2 * fx); fy = fy * fy * (3 - 2 * fy);
  const a = hash2(ix, iy), b = hash2(ix + 1, iy), c = hash2(ix, iy + 1), d = hash2(ix + 1, iy + 1);
  return lerp(lerp(a, b, fx), lerp(c, d, fx), fy);
}
// curl of the scalar field: (dN/dy, -dN/dx) - divergence free, so the whorls
// swirl around each other instead of piling up
function curl(x, y, out) {
  const e = 0.05;
  const dx = (vnoise(x + e, y) - vnoise(x - e, y)) / (2 * e);
  const dy = (vnoise(x, y + e) - vnoise(x, y - e)) / (2 * e);
  out[0] = dy; out[1] = -dx;
}

// =============================================================================
// SHADERS
// =============================================================================

// The reveal, shared by the smoke, the dust, the beam and the photo: how deep
// a plane point sits in the ray. uRayP is a point on the axis, uRayD its unit
// direction, uRayHalf the half width in world units; the rim is soft.
const REVEAL_CHUNK = `
  uniform vec2 uRayP;
  uniform vec2 uRayD;
  uniform float uRayHalf;
  float revealAt(vec2 p) {
    vec2 v = p - uRayP;
    float along = dot(v, uRayD);
    float across = abs(v.x * uRayD.y - v.y * uRayD.x);
    float inRay = 1.0 - smoothstep(uRayHalf * (1.0 - ${RAY_SOFT.toFixed(2)}), uRayHalf, across);
    // the ray only exists past the window's side
    inRay *= smoothstep(-2.0, 0.5, along);
    return ${RAY_OUTSIDE.toFixed(2)} + (1.0 - ${RAY_OUTSIDE.toFixed(2)}) * inRay;
  }
`;

const SAFE_CHUNK = `
${SAFE_ZONE_VERTEX_CHUNK}
  float safeAt(vec4 clip) {
    float a = safeZoneAttenuation(clip);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, clamp((a - 0.32) / 0.68, 0.0, 1.0));
  }
`;

const HASH_GLSL = `
  float hash21(vec2 p) {
    p = fract(p * vec2(0.3183, 0.3679)) + vec2(0.71, 0.113);
    p += dot(p, p.yx + 1.9);
    return fract(p.x * p.y * 7.31);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    vec2 s = i / 64.0;
    float a = hash21(s), b = hash21(s + vec2(1.0 / 64.0, 0.0));
    float c = hash21(s + vec2(0.0, 1.0 / 64.0)), d = hash21(s + vec2(1.0 / 64.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
`;

const ROOM_VERTEX = `
  precision mediump float;
  varying vec2 vWorld;
  void main() {
    vWorld = position.xy;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// The photograph: fitted by height, luminance capped, lifted where the ray
// passes, lit warm around the ember. Its own grain stays.
// TWO PICTURES: the background with the stick painted out (uMap, full frame)
// and the original (uMapStick), shown only inside the stick's rectangle from
// the holder up to the ember. Above the ember: the inpainted wall, nothing of
// the photo. The mask is measured HORIZONTALLY per row, exactly as the build
// cut it (an offset and a half width along x at the stick's slope), and cut
// along the axis at the ember with a 1 px feather.
const PHOTO_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uMap;
  uniform sampler2D uMapStick;
  uniform float uImgW;        // the image's world width
  uniform float uHH;
  uniform float uGain;        // cap / the source's bright end
  uniform float uRayLift;     // in-ray gain, relative
  uniform vec3 uRayColor;
  uniform vec2 uEmber;        // world - THE burn position, shared with the ash and the smoke
  uniform float uEmberGlow;   // 0..1, the ember's temperature
  uniform float uGlowR;       // world
  uniform vec2 uStickBase;    // world
  uniform vec2 uStickAxis;    // unit, up the stick
  uniform float uStickSlope;  // dx per dy of the axis
  uniform float uStickHalfX;  // half width of the mask, horizontally, world
  uniform float uStickOffX;   // the mask's centre, horizontally from the axis, world
  uniform float uFeather;     // world
  varying vec2 vWorld;
${REVEAL_CHUNK}
  void main() {
    // TextureLoader flips the picture (v = 1 is its top row), so v grows with y
    vec2 uv = vec2(vWorld.x / uImgW + 0.5, 0.5 + vWorld.y / (2.0 * uHH));
    uv = clamp(uv, 0.001, 0.999);
    vec3 wall = texture2D(uMap, uv).rgb;
    // the stick: inside the rectangle, and below the ember along the axis
    float xs = vWorld.x - (uStickBase.x + uStickSlope * (vWorld.y - uStickBase.y)) - uStickOffX;
    float across = 1.0 - smoothstep(uStickHalfX - uFeather, uStickHalfX + uFeather, abs(xs));
    float along = dot(vWorld - uStickBase, uStickAxis);
    float burnt = dot(uEmber - uStickBase, uStickAxis);
    float below = smoothstep(burnt + uFeather, burnt - uFeather, along)
                * smoothstep(-uFeather * 4.0, uFeather * 4.0, along + 6.0 * uFeather);
    float mask = across * below;
    vec3 stick = texture2D(uMapStick, uv).rgb;
    vec3 c = mix(wall, stick, mask) * uGain;
    float r = (revealAt(vWorld) - ${RAY_OUTSIDE.toFixed(2)}) / (1.0 - ${RAY_OUTSIDE.toFixed(2)});
    c *= mix(1.0, uRayLift, r) * mix(vec3(1.0), uRayColor, r * 0.5);
    // the ember lights the photo around it: warm, radial, breathing with it
    float d = distance(vWorld, uEmber) / uGlowR;
    float glow = exp(-d * d * 2.0) * uEmberGlow;
    c += vec3(1.0, 0.45, 0.12) * glow * 0.55 + c * glow * 1.2;
    gl_FragColor = vec4(c, 1.0);
  }
`;

// The fallback room: a dark wall, and the table.
const WALL_FRAGMENT = `
  precision mediump float;
  uniform vec3 uRayColor;
  uniform float uHH;
  varying vec2 vWorld;
${REVEAL_CHUNK}
${HASH_GLSL}
  void main() {
    float y = vWorld.y / uHH;
    float base = 0.018 + 0.010 * (1.0 - clamp(y * 0.5 + 0.5, 0.0, 1.0));
    float g = 0.97 + 0.06 * hash21(fract(vWorld.xy * 0.37));
    float r = revealAt(vWorld) - ${RAY_OUTSIDE.toFixed(2)};
    vec3 c = vec3(base * g) + uRayColor * r * 0.006;
    gl_FragColor = vec4(c, 1.0);
  }
`;
const TABLE_FRAGMENT = `
  precision mediump float;
  uniform vec3 uRayColor;
  uniform float uTop;
  varying vec2 vWorld;
${REVEAL_CHUNK}
${HASH_GLSL}
  void main() {
    float d = uTop - vWorld.y;
    float edge = smoothstep(0.0, 0.05, d);
    float grain = 0.94 + 0.12 * hash21(fract(vWorld.xy * 0.53));
    float r = revealAt(vWorld) - ${RAY_OUTSIDE.toFixed(2)};
    vec3 c = vec3(0.030 * grain) * (0.6 + 0.4 * edge) + uRayColor * r * 0.025 * edge;
    gl_FragColor = vec4(c, 1.0);
  }
`;
// The beam itself: a quad over the whole frame, additive, the reveal fading
// along its length, times a slow drift of brighter and darker air (tier 2).
const BEAM_FRAGMENT = `
  precision mediump float;
  uniform vec3 uRayColor;
  uniform float uTime;
  uniform float uHH;
  varying vec2 vWorld;
  varying vec4 vClip;
${REVEAL_CHUNK}
${HASH_GLSL}
${SAFE_CHUNK}
  void main() {
    float r = revealAt(vWorld) - ${RAY_OUTSIDE.toFixed(2)};
    float along = dot(vWorld - uRayP, uRayD);
    float fade = 1.0 - 0.6 * clamp(along / (4.5 * uHH), 0.0, 1.0);
#ifdef DETAIL
    float t = mod(uTime * 0.03, 16.0);
    float air = 0.7 + 0.3 * vnoise(vWorld.xy / uHH * 2.0 + vec2(t, -t * 0.6));
#else
    float air = 0.85;
#endif
    gl_FragColor = vec4(uRayColor * (r * fade * air * ${RAY_GAIN.toFixed(3)} * safeAt(vClip)), 1.0);
  }
`;
const BEAM_VERTEX = `
  precision mediump float;
  varying vec2 vWorld;
  varying vec4 vClip;
  void main() {
    vWorld = position.xy;
    vec4 clip = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    vClip = clip;
    gl_Position = clip;
  }
`;

// The whorls: instanced soft sprites. Each carries its position, size,
// rotation, stretch, alpha and seed; the fragment reads noise through a
// spiral so a whorl reads as a curl of smoke, torn into filaments.
const WHORL_VERTEX = `
  precision mediump float;
  attribute vec2 aPos;
  attribute vec2 aSizeRot;    // size, rotation
  attribute vec2 aStretchAlpha;
  attribute float aSeed;
  varying vec2 vUv;
  varying float vAlpha;
  varying float vSeed;
  varying vec4 vClip;
${REVEAL_CHUNK}
  void main() {
    vUv = position.xy;                     // -1..1
    float c = cos(aSizeRot.y), s = sin(aSizeRot.y);
    vec2 local = vec2(position.x * aStretchAlpha.x, position.y) * aSizeRot.x;
    vec2 w = aPos + vec2(local.x * c - local.y * s, local.x * s + local.y * c);
    // the cloud keeps more of itself outside the ray than the thread does
    float rv = (revealAt(aPos) - ${RAY_OUTSIDE.toFixed(2)}) / (1.0 - ${RAY_OUTSIDE.toFixed(2)});
    vAlpha = aStretchAlpha.y * (${WHORL_OUTSIDE.toFixed(2)} + (1.0 - ${WHORL_OUTSIDE.toFixed(2)}) * rv);
    vSeed = aSeed;
    vec4 clip = projectionMatrix * modelViewMatrix * vec4(w, 0.0, 1.0);
    vClip = clip;
    gl_Position = clip;
  }
`;
const WHORL_FRAGMENT = `
  precision mediump float;
  uniform vec3 uSmoke;
  uniform vec3 uRayColor;
  uniform float uTime;
  varying vec2 vUv;
  varying float vAlpha;
  varying float vSeed;
  varying vec4 vClip;
${HASH_GLSL}
${SAFE_CHUNK}
  void main() {
    float r = length(vUv);
    if (r > 1.0) discard;
    float disc = smoothstep(1.0, 0.1, r);
    float ang = r * (2.0 + 3.5 * fract(vSeed * 13.0)) + vSeed * 6.2831 + uTime * 0.12;
    mat2 R = mat2(cos(ang), -sin(ang), sin(ang), cos(ang));
    vec2 q = R * vUv * 1.5 + vec2(vSeed * 7.0, vSeed * 3.0);
    float t = mod(uTime * 0.05 + vSeed, 8.0);
#ifdef DETAIL
    float n = vnoise(q + vec2(t, 0.0)) * 0.6 + vnoise(q * 2.6 + vec2(0.0, t)) * 0.4;
#else
    float n = vnoise(q * 1.4 + vec2(t, 0.0));
#endif
    float body = disc * smoothstep(0.36, 0.72, n + 0.12 * disc);
    float a = body * vAlpha * safeAt(vClip);
    gl_FragColor = vec4(uSmoke * uRayColor, a);
  }
`;

const DUST_VERTEX = `
  precision mediump float;
  attribute float aPhase;
  varying float vA;
  varying vec4 vClip;
  uniform float uTime;
  uniform float uHH;
  uniform float uPx;
${REVEAL_CHUNK}
  void main() {
    vec3 p = position;
    p.y += sin(uTime * 0.21 + aPhase * 6.2831) * 0.08 * uHH * ${DUST_DRIFT.toFixed(3)} * 10.0;
    p.x += sin(uTime * 0.17 + aPhase * 9.1) * 0.06 * uHH * ${DUST_DRIFT.toFixed(3)} * 10.0;
    p.y -= mod(uTime * 0.012 * uHH + aPhase * 4.0 * uHH, 2.0 * uHH) - uHH;
    vec4 clip = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
    vClip = clip;
    gl_Position = clip;
    gl_PointSize = uPx;
    vA = (0.5 + 0.5 * sin(uTime * 0.7 + aPhase * 40.0)) * (revealAt(p.xy) - ${RAY_OUTSIDE.toFixed(2)});
  }
`;
const DUST_FRAGMENT = `
  precision mediump float;
  uniform vec3 uRayColor;
  varying float vA;
  varying vec4 vClip;
${SAFE_CHUNK}
  void main() {
    vec2 d = gl_PointCoord - 0.5;
    float a = smoothstep(0.5, 0.1, length(d));
    gl_FragColor = vec4(uRayColor * (a * vA * 0.30 * safeAt(vClip)), 1.0);
  }
`;

// Flat lit shapes on the plane: the fallback stick and holder, and the ash.
const STICK_VERTEX = `
  precision mediump float;
  varying vec2 vUv;
  varying vec2 vWorld;
  void main() {
    vUv = uv;
    vWorld = (modelMatrix * vec4(position, 1.0)).xy;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const STICK_FRAGMENT = `
  precision mediump float;
  uniform vec3 uColor;
  uniform float uRound;       // 1: shade like a cylinder across u
  uniform vec3 uRayColor;
  varying vec2 vUv;
  varying vec2 vWorld;
${REVEAL_CHUNK}
  void main() {
    float across = vUv.x * 2.0 - 1.0;
    float shade = mix(1.0, sqrt(max(0.0, 1.0 - across * across)) * 0.85 + 0.15, uRound);
    float lit = 0.35 + 0.65 * (revealAt(vWorld) - ${RAY_OUTSIDE.toFixed(2)});
    gl_FragColor = vec4(uColor * shade * (0.5 + 0.5 * lit) + uRayColor * lit * 0.02, 1.0);
  }
`;
// The ash: a thin strip of ASH_SEGMENTS quads along the stick, bent a little
// toward the ground, matte grey, with the ember's red fading into it over
// the first 6 px.
const ASH_VERTEX = `
  precision mediump float;
  attribute float aFrom;      // distance from the ember, world
  varying float vFrom;
  varying vec2 vWorld;
  void main() {
    vFrom = aFrom;
    vWorld = position.xy;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const ASH_FRAGMENT = `
  precision mediump float;
  uniform vec3 uEmberColor;
  uniform float uGradPx;
  uniform vec3 uRayColor;
  varying float vFrom;
  varying vec2 vWorld;
${REVEAL_CHUNK}
  void main() {
    float lit = 0.6 + 0.4 * (revealAt(vWorld) - ${RAY_OUTSIDE.toFixed(2)});
    vec3 grey = vec3(${ASH_GREY.toFixed(2)}, ${(ASH_GREY * 0.97).toFixed(2)}, ${(ASH_GREY * 0.92).toFixed(2)}) * lit;
    float k = clamp(vFrom / uGradPx, 0.0, 1.0);
    gl_FragColor = vec4(mix(uEmberColor, grey, k), 1.0);
  }
`;

// The ember: a small quad, the blackbody at a temperature that breathes.
const EMBER_FRAGMENT = `
  precision mediump float;
  uniform float uTemp;
  varying vec2 vUv;
${BLACKBODY_CHUNK}
  void main() {
    vec2 d = vUv * 2.0 - 1.0;
    float r = length(d);
    float core = smoothstep(1.0, 0.0, r);
    float t = uTemp * (0.55 + 0.45 * core * core);
    vec3 c = blackbody(t) * (0.35 + 1.4 * core * uTemp);
    float a = smoothstep(1.0, 0.6, r);
    gl_FragColor = vec4(c * a, a);
  }
`;
const EMBER_VERTEX = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// =============================================================================
// THE VISUALIZER
// =============================================================================

export class IncenseVisualizer extends Visualizer {
  constructor(options) {
    super();
    const opts = options || {};
    let tier = opts.qualityTier != null ? opts.qualityTier : DEFAULT_TIER;
    if (!TIER[tier]) tier = 0;
    this._tier = tier;
    this._layout = TIER[tier];

    this._time = 0;
    this._paused = false;
    this._energy = ENERGY_DEFAULT;
    this._transition = TRANSITION_MAX - TRANSITION_SPAN * ENERGY_DEFAULT;
    this._midBand = 0;
    this._progress = 0;
    this._lastPalette = null;
    this._emberTemp = 0.6;
    this._flareAt = rand(EMBER_FLARE_EVERY[0], EMBER_FLARE_EVERY[1]);
    this._flareT = -1;
    this._burn = 0;                // how far along the stick the ember has receded (0..1 of tip->base)
    this._ashLen = 0;              // the ash above the ember, as a fraction of the stick (0..ASH_MAX)
    this._ashStart = 0;            // when the current ash began growing
    this._ashPeriod = rand(ASH_PERIOD[0], ASH_PERIOD[1]);
    this._ashFalling = -1;         // time the fall began
    this._ashFalls = 0;
    this._whorlAcc = 0;
    this._aspect = 0;
    this._curlTmp = [0, 0];
    this._meta = FALLBACK_META;
    this._hasPhoto = false;
    this._emberW = new THREE.Vector2();
    this._tearX = 0; this._tearY = 0;

    const h = urlParam('incenseheight');
    this._forcedHeight = h != null && h !== '' ? clamp01(parseFloat(h)) : null;
    this._forceAsh = urlParam('incenseash') === '1';
    const pr = urlParam('incenseprogress');
    this._forcedProgress = pr != null && pr !== '' ? clamp01(parseFloat(pr)) : null;
    this._tipDebug = urlParam('incensetip') === '1';
    this._postProcessing = { bloomStrength: 0.35, vignette: 0.40, aberration: 0.0008, grain: 0.03 };
  }

  get postProcessing() { return this._postProcessing; }

  // image space (fractions, y down) -> world, the photo fitted by height
  _imgToWorld(u, v, out) {
    out.set((u - 0.5) * this._imgW, (0.5 - v) * 2 * this._HH);
    return out;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const L = this._layout;
    camera.position.set(0, 0, CAMERA_Z);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
    camera.updateMatrixWorld();

    const HH = CAMERA_Z * Math.tan(camera.fov * Math.PI / 360);
    this._HH = HH;
    this._aspect = safeZoneUniforms.uAspect.value || 1.78;
    const HW = HH * this._aspect;
    this._HW = HW;
    this._imgW = 2 * HH * SCENE_ASPECT;
    this._px = 2 * HH / 720;               // one 720p pixel, in world units

    this._shared = {
      uRayP: { value: new THREE.Vector2() },
      uRayD: { value: new THREE.Vector2() },
      uRayHalf: { value: 1 },
      uRayColor: { value: new THREE.Vector3(1.0, 0.93, 0.82) },
      uTime: { value: 0 },
      uHH: { value: HH }
    };
    this._objects = [];
    const mk = (geo, mat, order) => { const m = new THREE.Mesh(geo, mat); m.frustumCulled = false; m.renderOrder = order; scene.add(m); this._objects.push(m); return m; };

    // --- the fallback room and stick (hidden unless the photo fails)
    this._wall = mk(new THREE.PlaneGeometry(HW * 2.2, HH * 2.2), new THREE.ShaderMaterial({
      uniforms: Object.assign({}, this._shared), vertexShader: ROOM_VERTEX, fragmentShader: WALL_FRAGMENT
    }), 0);
    this._wall.position.z = -0.05;
    this._wall.visible = false;
    const stickMat = (color, round) => new THREE.ShaderMaterial({
      uniforms: Object.assign({ uColor: { value: new THREE.Vector3(...color) }, uRound: { value: round } }, this._shared),
      vertexShader: STICK_VERTEX, fragmentShader: STICK_FRAGMENT
    });
    this._stickMat = stickMat;
    this._fallback = new THREE.Group();
    scene.add(this._fallback);
    this._fallback.visible = false;

    // --- the photo
    this._photoUniforms = Object.assign({
      uMap: { value: null }, uMapStick: { value: null }, uImgW: { value: this._imgW }, uGain: { value: 1 },
      uRayLift: { value: PHOTO_IN_RAY / PHOTO_CAP },
      uEmber: { value: new THREE.Vector2() }, uEmberGlow: { value: 0 }, uGlowR: { value: EMBER_GLOW_PX * this._px },
      uStickBase: { value: new THREE.Vector2() }, uStickAxis: { value: new THREE.Vector2(0, 1) },
      uStickSlope: { value: 0 }, uStickHalfX: { value: 0 }, uStickOffX: { value: 0 },
      uFeather: { value: MASK_FEATHER_PX * this._px }
    }, this._shared);
    this._photo = mk(new THREE.PlaneGeometry(HW * 2.2, HH * 2.2), new THREE.ShaderMaterial({
      uniforms: this._photoUniforms, vertexShader: ROOM_VERTEX, fragmentShader: PHOTO_FRAGMENT
    }), 1);
    this._photo.position.z = -0.03;
    this._photo.visible = false;

    // --- the placement, from the fallback's measurements; redone when the manifest lands
    this._place(FALLBACK_META);

    // --- the ash, the falling piece, the ember
    this._ashUniforms = Object.assign({ uEmberColor: { value: new THREE.Vector3(0.6, 0.1, 0.02) }, uGradPx: { value: ASH_EMBER_PX * this._px } }, this._shared);
    const aGeo = new THREE.BufferGeometry();
    this._ashPos = new THREE.BufferAttribute(new Float32Array((ASH_SEGMENTS + 1) * 2 * 3), 3);
    this._ashFrom_ = new THREE.BufferAttribute(new Float32Array((ASH_SEGMENTS + 1) * 2), 1);
    this._ashPos.setUsage(THREE.DynamicDrawUsage);
    this._ashFrom_.setUsage(THREE.DynamicDrawUsage);
    aGeo.setAttribute('position', this._ashPos);
    aGeo.setAttribute('aFrom', this._ashFrom_);
    const idx = [];
    for (let i = 0; i < ASH_SEGMENTS; i++) { const b = i * 2; idx.push(b, b + 1, b + 2, b + 1, b + 3, b + 2); }
    aGeo.setIndex(idx);
    this._ashMesh = mk(aGeo, new THREE.ShaderMaterial({ uniforms: this._ashUniforms, vertexShader: ASH_VERTEX, fragmentShader: ASH_FRAGMENT, side: THREE.DoubleSide }), 4);
    const pGeo = new THREE.PlaneGeometry(STICK_PX * this._px, 1);
    pGeo.translate(0, 0.5, 0);
    this._ashPiece = mk(pGeo, stickMat([ASH_GREY, ASH_GREY * 0.97, ASH_GREY * 0.92], 1), 4);
    this._ashPiece.visible = false;
    this._emberMat = new THREE.ShaderMaterial({
      uniforms: { uTemp: { value: 0.6 } }, vertexShader: EMBER_VERTEX, fragmentShader: EMBER_FRAGMENT,
      transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false
    });
    // The ember's quad is drawn 1.4x its radius, not 2.2x: it is additive and
    // sits on top of the ash, and at 2.2x (19 px across at 720p) it covered
    // the whole of a 3% ash - measured, the ash's midpoint read the ember's
    // orange, not grey. The core is unchanged; only the quad's reach is.
    this._ember = mk(new THREE.PlaneGeometry(EMBER_R * 2 * HH * 1.4, EMBER_R * 2 * HH * 1.4), this._emberMat, 6);

    // --- the beam
    this._beam = mk(new THREE.PlaneGeometry(HW * 2.2, HH * 2.2), new THREE.ShaderMaterial({
      uniforms: Object.assign({}, this._shared, safeZoneUniforms), vertexShader: BEAM_VERTEX, fragmentShader: BEAM_FRAGMENT,
      defines: this._tier >= 2 ? { DETAIL: 1 } : {},
      transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false
    }), 5);

    // --- the ribbon (laminar smoke) and its twin strand, the whorls, the dust
    this._ribbon = this._buildStrip(RIBBON_SAMPLES, 7);
    this._twin = this._buildStrip(TWIN_SAMPLES, 7);
    this._buildWhorls(L.whorls);
    if (L.dust > 0) this._buildDust(L.dust);
    if (this._tipDebug) this._buildTipDebug();

    this._loadPhoto();
    this._advanceAsh(0);
  }

  // Everything that hangs off the three measured points.
  _place(meta) {
    this._meta = meta;
    const HH = this._HH;
    this._tipW = this._imgToWorld(meta.tip.x, meta.tip.y, new THREE.Vector2());
    this._baseW = this._imgToWorld(meta.base.x, meta.base.y, new THREE.Vector2());
    // the stick's axis from the measured angle: leaning right, so it runs up
    // and to the right from the base
    const a = meta.angle * Math.PI / 180;
    this._axis = new THREE.Vector2(Math.sin(a), Math.cos(a));
    this._stickLen = this._tipW.distanceTo(this._baseW);
    // the stick's rectangle, from the manifest's measurements: horizontal
    // half width and offset per row, in source px -> world
    const st = meta.stick || FALLBACK_META.stick;
    const pxImg = this._imgW / (meta.sourceW || FALLBACK_META.sourceW);
    this._stickHalfX = st.maskHalfPx * pxImg;
    this._stickOffX = (st.maskOffsetPx || 0) * pxImg;
    this._stickTopW = this._imgToWorld(st.extremity.x, st.extremity.y, new THREE.Vector2());
    const pu = this._photoUniforms;
    pu.uStickBase.value.copy(this._baseW);
    pu.uStickAxis.value.copy(this._axis);
    pu.uStickSlope.value = this._axis.x / this._axis.y;
    pu.uStickHalfX.value = this._stickHalfX;
    pu.uStickOffX.value = this._stickOffX;
    // the ray: crossing the smoke's path between 30% and 60% of its height,
    // coming down from the right at 45 degrees
    const air = HH - this._tipW.y;
    const aimY = this._tipW.y + air * (RAY_CROSS[0] + RAY_CROSS[1]) / 2;
    const th = RAY_ANGLE_DEG * Math.PI / 180;
    const dir = new THREE.Vector2(-Math.cos(th), -Math.sin(th));
    const aim = new THREE.Vector2(this._tipW.x, aimY);
    const from = aim.clone().sub(dir.clone().multiplyScalar(6 * HH));
    this._rayFrom = from; this._rayDir = dir;
    this._shared.uRayP.value.copy(from);
    this._shared.uRayD.value.copy(dir);
    // the half width: the band's vertical extent at the column is 30% of the air
    this._shared.uRayHalf.value = (RAY_CROSS[1] - RAY_CROSS[0]) * air * Math.sin(th) / 2;
    this._rebuildFallbackStick();
    if (this._tipMarks) this._layTipDebug();
  }

  _rebuildFallbackStick() {
    const g = this._fallback;
    while (g.children.length) { const c = g.children.pop(); c.geometry.dispose(); c.material.dispose(); }
    const HH = this._HH;
    const holderY = this._baseW.y;
    const tableY = holderY - HOLDER_H * HH;
    const tableH = tableY + 1.1 * HH;
    const table = new THREE.Mesh(new THREE.PlaneGeometry(this._HW * 2.2, tableH), new THREE.ShaderMaterial({
      uniforms: Object.assign({ uTop: { value: tableY } }, this._shared), vertexShader: ROOM_VERTEX, fragmentShader: TABLE_FRAGMENT
    }));
    table.position.set(0, tableY - tableH / 2, -0.02);
    const holder = new THREE.Mesh(new THREE.PlaneGeometry(HOLDER_W * 2 * HH, HOLDER_H * 2 * HH), this._stickMat([0.16, 0.14, 0.12], 1));
    holder.position.set(this._baseW.x, holderY, 0.01);
    const len = this._stickLen * 1.08;
    const sGeo = new THREE.PlaneGeometry(STICK_PX * this._px, len);
    sGeo.translate(0, len / 2, 0);
    const stick = new THREE.Mesh(sGeo, this._stickMat([0.20, 0.10, 0.06], 1));
    stick.position.set(this._baseW.x, this._baseW.y, 0.02);
    stick.rotation.z = -this._meta.angle * Math.PI / 180;
    for (const m of [table, holder, stick]) { m.frustumCulled = false; m.renderOrder = 2; g.add(m); }
  }

  _loadPhoto() {
    const self = this;
    const want = this._layout.photo;
    fetch(SCENE_MANIFEST)
      .then(function (r) { if (!r.ok) throw new Error('no manifest'); return r.json(); })
      .then(function (m) {
        if (!self._scene) return null;
        if (m.tip && m.base && m.angle != null) {
          self._place({ tip: m.tip, base: m.base, angle: m.angle, stick: m.stick || FALLBACK_META.stick,
                        sourceW: (m.sourceSize && m.sourceSize[0]) || FALLBACK_META.sourceW });
        }
        self._photoUniforms.uGain.value = PHOTO_CAP / Math.max(0.05, m.p99Luma || 0.5) * (self._tipDebug ? 2 : 1);
        const near = function (role) {
          return (m.variants || []).filter(function (v) { return (v.role || 'photo') === role; })
            .sort(function (a, b) { return Math.abs(a.width - want) - Math.abs(b.width - want); })[0];
        };
        const v = near('photo'), vn = near('nostick');
        if (!v) throw new Error('no variant');
        const load = function (f) { return new Promise(function (res, rej) { new THREE.TextureLoader().load('textures/' + f, res, undefined, rej); }); };
        // WITHOUT THE INPAINTED BACKGROUND (an older manifest), the original
        // stands in for it and the stick never shortens - said in the console
        // rather than left to be noticed.
        if (!vn) console.warn('[incense] manifest has no nostick variant: the stick will not shorten (run build-scenes)');
        return Promise.all([load(v.file), vn ? load(vn.file) : null]);
      })
      .then(function (pair) {
        if (!pair || !self._scene) return;
        const tex = pair[0], nos = pair[1];
        for (const t of [tex, nos]) {
          if (!t) continue;
          t.colorSpace = THREE.SRGBColorSpace;
          t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
          t.minFilter = THREE.LinearMipmapLinearFilter;
          t.anisotropy = 4;
        }
        self._photoTex = tex;
        self._nostickTex = nos;
        self._photoUniforms.uMap.value = nos || tex;
        self._photoUniforms.uMapStick.value = tex;
        self._photo.visible = true;
        self._wall.visible = false;
        self._fallback.visible = false;
        self._hasPhoto = true;
      })
      .catch(function (err) {
        if (!self._scene) return;
        console.warn('[incense] no incense-scene photo (' + (err && err.message) + '): procedural stick and holder instead');
        self._fallback.visible = true;
        self._wall.visible = true;
      });
  }

  _buildTipDebug() {
    const mat = new LineMaterial({ linewidth: 2, color: 0xff2020, transparent: true, depthTest: false, depthWrite: false, dashed: true, dashSize: 1e9, gapSize: 0, alphaToCoverage: false });
    mat.resolution.set(1280, 720);
    const geo = new LineSegmentsGeometry();
    this._tipPos = new Float32Array(7 * 6);    // 3 crosses (2 segments each) + the stick
    geo.setPositions(this._tipPos);
    const mesh = new LineSegments2(geo, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = 20;
    this._scene.add(mesh);
    // the stick's mask, translucent red: it must cover the photographed stick
    // exactly, rim included, from the holder to past the pale end - the same
    // rectangle the build inpainted and the shader reveals below the ember
    const qGeo = new THREE.PlaneGeometry(1, 1);
    qGeo.translate(0, 0.5, 0);
    const quad = new THREE.Mesh(qGeo, new THREE.MeshBasicMaterial({ color: 0xff2020, transparent: true, opacity: 0.35, depthTest: false, depthWrite: false }));
    quad.frustumCulled = false;
    quad.renderOrder = 19;
    this._scene.add(quad);
    this._objects.push(quad);
    this._tipMarks = { mesh: mesh, geo: geo, mat: mat, quad: quad };
    this._objects.push(mesh);
    this._layTipDebug();
  }
  _layTipDebug() {
    const P = this._tipPos, c = 10 * this._px;
    const t = this._tipW, b = this._baseW;
    const seg = (k, x0, y0, x1, y1) => { P[k * 6] = x0; P[k * 6 + 1] = y0; P[k * 6 + 2] = 0.2; P[k * 6 + 3] = x1; P[k * 6 + 4] = y1; P[k * 6 + 5] = 0.2; };
    seg(0, t.x - c, t.y, t.x + c, t.y); seg(1, t.x, t.y - c, t.x, t.y + c);
    seg(2, b.x - c, b.y, b.x + c, b.y); seg(3, b.x, b.y - c, b.x, b.y + c);
    seg(4, t.x, t.y, b.x, b.y);
    // the third mark: where the ember is now (the smoke leaves from here)
    const e = this._emberW, d = c * 0.7;
    seg(5, e.x - d, e.y - d, e.x + d, e.y + d); seg(6, e.x - d, e.y + d, e.x + d, e.y - d);
    this._tipMarks.geo.setPositions(this._tipPos);
    // the mask quad: the horizontal half width and offset become perpendicular
    // ones by cos(angle); it runs from the base to a little past the extremity
    const q = this._tipMarks.quad, a = this._meta.angle * Math.PI / 180, ca = Math.cos(a);
    const len = this._stickTopW ? this._stickTopW.distanceTo(b) + 4 * this._px : this._stickLen;
    q.scale.set(2 * this._stickHalfX * ca, len, 1);
    const nx = this._axis.y, ny = -this._axis.x;                 // across, to the right
    const off = this._stickOffX * ca;
    q.position.set(b.x + nx * off, b.y + ny * off, 0.15);
    q.rotation.z = -a;
  }

  // The reveal on the CPU, for the ribbon's per-vertex alpha.
  _revealAt(x, y) {
    const vx = x - this._rayFrom.x, vy = y - this._rayFrom.y;
    const along = vx * this._rayDir.x + vy * this._rayDir.y;
    const across = Math.abs(vx * this._rayDir.y - vy * this._rayDir.x);
    const half = this._shared.uRayHalf.value;
    const t = clamp01((across - half * (1 - RAY_SOFT)) / (half * RAY_SOFT));
    let inRay = 1 - t * t * (3 - 2 * t);
    const u = clamp01((along + 2) / 2.5);
    inRay *= u * u * (3 - 2 * u);
    return RAY_OUTSIDE + (1 - RAY_OUTSIDE) * inRay;
  }
  _safeAt(x, y) {
    const c = safeZoneUniforms.uSafeCenter.value, h = safeZoneUniforms.uSafeHalfSize.value;
    const feather = safeZoneUniforms.uSafeFeather.value;
    const sx = x / this._HH, sy = y / this._HH;
    const qx = Math.abs(sx - c.x) - h.x, qy = Math.abs(sy - c.y) - h.y;
    const d = Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) + Math.min(Math.max(qx, qy), 0);
    const t = clamp01(d / feather);
    return lerp(SAFE_FLOOR, 1, t * t * (3 - 2 * t));
  }

  // A fat-line strip of N samples, dynamic, soft-edged (drawn 1 px wider
  // each side, alpha ramping over it), never round-capped.
  _buildStrip(N, order) {
    const mat = new LineMaterial({
      linewidth: RIBBON_PX + 2, vertexColors: true, transparent: true, opacity: RIBBON_ALPHA,
      blending: THREE.NormalBlending, depthTest: false, depthWrite: false,
      dashed: true, dashSize: 1e9, gapSize: 0, alphaToCoverage: false
    });
    mat.resolution.set(1280, 720);
    const halfPx = { value: (RIBBON_PX + 2) / 2 };
    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uHalfPx = halfPx;
      shader.fragmentShader = 'uniform float uHalfPx;\n' + shader.fragmentShader.replace(
        'gl_FragColor = vec4( diffuseColor.rgb, alpha );',
        'alpha *= clamp((1.0 - abs(vUv.x)) * uHalfPx, 0.0, 1.0);\n\t\t\tgl_FragColor = vec4( diffuseColor.rgb, alpha );'
      );
    };
    mat.customProgramCacheKey = () => 'incense-aa';
    const geo = new LineSegmentsGeometry();
    geo.setPositions(new Float32Array((N - 1) * 6));
    geo.setColors(new Float32Array((N - 1) * 6));
    const posBuf = geo.attributes.instanceStart.data, colBuf = geo.attributes.instanceColorStart.data;
    posBuf.setUsage(THREE.DynamicDrawUsage);
    colBuf.setUsage(THREE.DynamicDrawUsage);
    const mesh = new LineSegments2(geo, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = order;
    const self = this;
    mesh.onBeforeRender = function (renderer) { self._onFrame(renderer); };
    this._scene.add(mesh);
    this._objects.push(mesh);
    return { mesh, geo, mat, posBuf, colBuf, N, x: new Float32Array(N), y: new Float32Array(N) };
  }

  _buildWhorls(count) {
    this._whorlN = count;
    const geo = new THREE.InstancedBufferGeometry();
    const quad = new Float32Array([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]);
    geo.setAttribute('position', new THREE.BufferAttribute(quad, 3));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    this._wPos = new THREE.InstancedBufferAttribute(new Float32Array(count * 2), 2);
    this._wSizeRot = new THREE.InstancedBufferAttribute(new Float32Array(count * 2), 2);
    this._wStretchAlpha = new THREE.InstancedBufferAttribute(new Float32Array(count * 2), 2);
    this._wSeed = new THREE.InstancedBufferAttribute(new Float32Array(count), 1);
    for (const a of [this._wPos, this._wSizeRot, this._wStretchAlpha]) a.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aPos', this._wPos);
    geo.setAttribute('aSizeRot', this._wSizeRot);
    geo.setAttribute('aStretchAlpha', this._wStretchAlpha);
    geo.setAttribute('aSeed', this._wSeed);
    geo.instanceCount = count;
    this._whorlMat = new THREE.ShaderMaterial({
      uniforms: Object.assign({ uSmoke: { value: new THREE.Vector3(...SMOKE_COLOR) } }, this._shared, safeZoneUniforms),
      vertexShader: WHORL_VERTEX, fragmentShader: WHORL_FRAGMENT,
      defines: this._tier >= 2 ? { DETAIL: 1 } : {},
      transparent: true, blending: THREE.NormalBlending, depthWrite: false, depthTest: false
    });
    this._whorls = new THREE.Mesh(geo, this._whorlMat);
    this._whorls.frustumCulled = false;
    this._whorls.renderOrder = 8;
    this._scene.add(this._whorls);
    this._objects.push(this._whorls);
    this._w = { x: new Float32Array(count), y: new Float32Array(count), age: new Float32Array(count).fill(-1), life: new Float32Array(count),
      size: new Float32Array(count), rot: new Float32Array(count), spin: new Float32Array(count), vx: new Float32Array(count), vy: new Float32Array(count) };
    for (let i = 0; i < count; i++) this._wSeed.array[i] = Math.random();
    this._whorlNext = 0;
  }

  _buildDust(count) {
    const HH = this._HH, HW = this._HW;
    const pos = new Float32Array(count * 3);
    const phase = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = rand(-HW, HW); pos[i * 3 + 1] = rand(-HH, HH); pos[i * 3 + 2] = 0.1;
      phase[i] = Math.random();
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aPhase', new THREE.BufferAttribute(phase, 1));
    this._dustMat = new THREE.ShaderMaterial({
      uniforms: Object.assign({ uPx: { value: 2.0 } }, this._shared, safeZoneUniforms),
      vertexShader: DUST_VERTEX, fragmentShader: DUST_FRAGMENT,
      transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false
    });
    this._dust = new THREE.Points(geo, this._dustMat);
    this._dust.frustumCulled = false;
    this._dust.renderOrder = 6;
    this._scene.add(this._dust);
    this._objects.push(this._dust);
  }

  // --- the frame ---------------------------------------------------------------

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.1);
    this._paused = profile.playbackState === 'PAUSED';
    if (this._paused) return;
    this._time += dt;
    this._shared.uTime.value = this._time;

    const palette = profile.artworkPalette;
    if (palette !== this._lastPalette) { this._lastPalette = palette; this._setRayColor(profile); }

    // the energy: the analysis timeline's current segment first, the
    // catalogue's static number next, a default last - through a slow filter
    let e = null;
    if (profile.analysisTimeline && profile.analysisTimeline.lookupEnergy) {
      const now = profile.estimatedPosition != null ? profile.estimatedPosition : profile.position;
      if (now != null) e = profile.analysisTimeline.lookupEnergy(now * 1000);
    }
    if (e == null && profile.trackFeatures && profile.trackFeatures.energy != null) e = profile.trackFeatures.energy;
    if (e == null) e = ENERGY_DEFAULT * 100;
    if (e > 1) e /= 100;
    e = clamp01(e);
    this._energy += (e - this._energy) * Math.min(1, dt / ENERGY_TAU);
    this._transition = this._forcedHeight != null ? this._forcedHeight : TRANSITION_MAX - TRANSITION_SPAN * this._energy;

    const director = profile.director || {};
    const live = director.hasReliableGrid && director.bands;
    const mid = live ? clamp01(director.bands.mid) : 0;
    this._midBand += (mid - this._midBand) * Math.min(1, dt / 0.8);
    this._progress = this._forcedProgress != null ? this._forcedProgress : clamp01(profile.progress || 0);

    this._advanceEmber(dt);
    this._advanceAsh(dt);
    this._advanceRibbon(dt);
    this._advanceWhorls(dt);
  }

  _setRayColor(profile) {
    const palette = profile.artworkPalette;
    const vivid = palette && palette.vividColors && palette.vividColors.length ? palette.vividColors[0] : null;
    let r = 1.0, g = 0.93, b = 0.82;      // warm, near white
    if (vivid != null) {
      const pr = ((vivid >> 16) & 255) / 255, pg = ((vivid >> 8) & 255) / 255, pb = (vivid & 255) / 255;
      const mx = Math.max(pr, pg, pb, 1e-3);
      r = lerp(r, pr / mx, 0.2); g = lerp(g, pg / mx, 0.2); b = lerp(b, pb / mx, 0.2);
      const m = Math.max(r, g, b); r /= m; g /= m; b /= m;
    }
    this._shared.uRayColor.value.set(r, g, b);
  }

  _advanceEmber(dt) {
    const n = vnoise(this._time * 0.35, 3.7) * 0.5 + vnoise(this._time * 0.11, 8.1) * 0.5;
    let temp = 0.45 + 0.30 * n;
    this._flareAt -= dt;
    if (this._flareAt <= 0) { this._flareT = 0; this._flareAt = rand(EMBER_FLARE_EVERY[0], EMBER_FLARE_EVERY[1]); }
    if (this._flareT >= 0) {
      const f = this._flareT / EMBER_FLARE_S;
      temp += 0.45 * Math.sin(Math.PI * clamp01(f));
      this._flareT += dt;
      if (this._flareT > EMBER_FLARE_S) this._flareT = -1;
    }
    this._emberTemp = clamp01(temp);
    this._emberMat.uniforms.uTemp.value = this._emberTemp;
    this._photoUniforms.uEmberGlow.value = this._emberTemp;
    // the same blackbody ramp as the ember shader, for the ash's base
    const tt = this._emberTemp * 0.8;
    const sm = (a, b, x) => { const k = clamp01((x - a) / (b - a)); return k * k * (3 - 2 * k); };
    this._ashUniforms.uEmberColor.value.set(sm(0.02, 0.28, tt), sm(0.22, 0.72, tt) * 0.9, sm(0.58, 1.0, tt)).multiplyScalar(0.9);
  }

  // The ember recedes along the stick with progress. ONE position - _emberW -
  // drives the ember quad, the photo's stick mask (uEmber), the ash's root and
  // the smoke's origin (_advanceRibbon reads _emberW). The ash above it grows
  // on its own clock to ASH_MAX and then breaks off, falls to the holder in
  // half a second with a puff of smoke, and starts again from nothing.
  _advanceAsh(dt) {
    const tip = this._tipW, base = this._baseW;
    this._burn = this._progress * BURN_SHARE;
    const ex = lerp(tip.x, base.x, this._burn), ey = lerp(tip.y, base.y, this._burn);
    this._emberW.set(ex, ey);
    this._ember.position.set(ex, ey, 0.04);
    this._photoUniforms.uEmber.value.set(ex, ey);

    const u = clamp01((this._time - this._ashStart) / this._ashPeriod);
    this._ashLen = u < ASH_QUICK ? ASH_FIRST * (u / ASH_QUICK)
                                 : ASH_FIRST + (ASH_MAX - ASH_FIRST) * (u - ASH_QUICK) / (1 - ASH_QUICK);
    if ((u >= 1 || this._forceAsh) && this._ashFalling < 0 && this._ashLen > 0.005) {
      this._forceAsh = false;
      this._ashFalls++;
      this._ashFalling = this._time;
      this._ashPiece.scale.y = this._ashLen * this._stickLen;
      this._pieceFrom = new THREE.Vector2(ex, ey);
      this._ashPiece.visible = true;
      this._ashStart = this._time;
      this._ashPeriod = rand(ASH_PERIOD[0], ASH_PERIOD[1]);
      this._ashLen = 0;
      // a puff of smoke as it breaks off
      for (let k = 0; k < ASH_PUFF; k++) this._spawnWhorl(ex + rand(-0.02, 0.02) * this._HH, ey + rand(0, 0.02) * this._HH);
    }
    if (this._ashFalling >= 0) {
      const t = (this._time - this._ashFalling) / ASH_FALL_S;
      if (t >= 1) { this._ashFalling = -1; this._ashPiece.visible = false; }
      else {
        // it drops toward the holder: gravity, and a tumble
        const s = t * t;
        const x = lerp(this._pieceFrom.x, base.x + 0.02 * this._HH, s) + 0.03 * this._HH * t;
        const y = lerp(this._pieceFrom.y, base.y + HOLDER_H * this._HH, s);
        this._ashPiece.position.set(x, y, 0.03);
        this._ashPiece.rotation.z = -this._meta.angle * Math.PI / 180 + 2.4 * t;
      }
    }
    this._layAsh(ex, ey);
    if (this._tipMarks) this._layTipDebug();
  }

  // The ash strip: from the ember up the stick's axis by the ash's length,
  // the stick's width, bending a little toward the ground along its length,
  // the far end drooping most, as ash does before it falls.
  _layAsh(ex, ey) {
    const len = this._ashLen * this._stickLen;
    const P = this._ashPos.array, F = this._ashFrom_.array;
    const ax = this._axis.x, ay = this._axis.y;                        // up the stick
    const nx = ay, ny = -ax;                                           // across, to the right
    const hw = STICK_PX * this._px / 2;
    const visible = len > 0.5 * this._px;
    for (let i = 0; i <= ASH_SEGMENTS; i++) {
      const k = i / ASH_SEGMENTS, d = visible ? k * len : 0;
      // the droop: down (world -y), growing with the square of the distance
      const sag = ASH_SAG * len * Math.pow(k, 1.6);
      const cx = ex + ax * d, cy = ey + ay * d - sag;
      const o = i * 6;
      P[o] = cx - nx * hw; P[o + 1] = cy - ny * hw; P[o + 2] = 0.03;
      P[o + 3] = cx + nx * hw; P[o + 4] = cy + ny * hw; P[o + 5] = 0.03;
      F[i * 2] = d; F[i * 2 + 1] = d;
    }
    this._ashPos.needsUpdate = true;
    this._ashFrom_.needsUpdate = true;
    this._ashMesh.visible = visible;
  }

  // The thread: samples from the ember to the transition height. It leaves
  // along the stick's axis for a few centimetres and straightens; the
  // lateral offset is a slow sinuous sway travelling up with the smoke, and
  // over the top 20% the instability: a wobble whose amplitude grows to the
  // break, a twin strand peeling off, and the thread fading into the whorls.
  _advanceRibbon(dt) {
    const HH = this._HH, px = this._px;
    const x0 = this._emberW.x, y0 = this._emberW.y;
    const top = y0 + this._transition * (HH - y0);
    const H = Math.max(0.05, top - y0);
    const sway = (SWAY_PX + SWAY_MID_PX * this._midBand) * px;
    const t = this._time;
    const s0 = 1 - INSTABILITY;
    const L0 = AXIS_RUN * H / 3;           // e^-3 at 15%: vertical from there
    const tanA = Math.tan(this._meta.angle * Math.PI / 180);
    const rc = this._shared.uRayColor.value;
    const self = this;
    // the wobble's amplitude follows the unstable stretch's own height, so a
    // short thread ends in a gentle S and not a zigzag: a quarter of it, 22 px at most
    const growMax = Math.min(22 * px, 0.25 * INSTABILITY * H) * (1 + 0.6 * this._midBand);

    // lateral position of the thread at height fraction s, and of its twin
    function xAt(s, twin) {
      const dy = s * H;
      // along the axis first, then vertical: an exponential approach
      let x = x0 + tanA * L0 * (1 - Math.exp(-dy / L0));
      const phase = (t - dy / (2 * HH / RISE_TIME)) / SWAY_PERIOD * Math.PI * 2;
      x += Math.sin(phase) * sway * s + Math.sin(phase * 0.37 + 1.3) * sway * 0.5 * s;
      if (s > s0) {
        const u = (s - s0) / INSTABILITY;
        const grow = u * u * (3 - 2 * u) * growMax;
        const sign = twin ? -1 : 1;
        x += sign * (Math.sin(phase * 2.2 + u * 7.5) * grow + Math.sin(phase * 3.7 + u * 13.0) * grow * 0.4);
      }
      return x;
    }
    function fill(strip, sFrom, twin, alphaAt) {
      const N = strip.N, P = strip.posBuf.array, C = strip.colBuf.array;
      let ix = 0, ic = 0, lastX = 0, lastY = 0;
      for (let i = 0; i < N; i++) {
        const k = i / (N - 1);
        const s = sFrom + (1 - sFrom) * (1 - Math.pow(1 - k, 1.6));   // samples crowd toward the top
        const y = y0 + s * H;
        const x = xAt(s, twin);
        const a = self._revealAt(x, y) * self._safeAt(x, y) * alphaAt(s);
        if (i > 0) {
          P[ix++] = lastX; P[ix++] = lastY; P[ix++] = 0.05;
          P[ix++] = x; P[ix++] = y; P[ix++] = 0.05;
          const r = SMOKE_COLOR[0] * rc.x * a, g = SMOKE_COLOR[1] * rc.y * a, b = SMOKE_COLOR[2] * rc.z * a;
          C[ic++] = r; C[ic++] = g; C[ic++] = b; C[ic++] = r; C[ic++] = g; C[ic++] = b;
        }
        lastX = x; lastY = y;
        strip.x[i] = x; strip.y[i] = y;
      }
      strip.posBuf.needsUpdate = true;
      strip.colBuf.needsUpdate = true;
      return [lastX, lastY];
    }
    // the main thread: full alpha, fading out over the last 8% (no angle at the break)
    const tear = fill(this._ribbon, 0, false, (s) => 1 - clamp01((s - 0.92) / 0.08));
    // the twin: peels off through the unstable stretch, fades the same way
    fill(this._twin, s0, true, (s) => { const u = (s - s0) / INSTABILITY; return clamp01(u * 2.5) * 0.5 * (1 - clamp01((s - 0.92) / 0.08)); });
    this._tearX = tear[0]; this._tearY = tear[1];
  }

  _spawnWhorl(x, y) {
    const W = this._w, n = this._whorlN, HH = this._HH;
    const i = this._whorlNext; this._whorlNext = (this._whorlNext + 1) % n;
    W.x[i] = x; W.y[i] = y;
    W.age[i] = 0; W.life[i] = WHORL_LIFE * rand(0.85, 1.15);
    W.size[i] = rand(WHORL_SIZE[0], WHORL_SIZE[1]) * 2 * HH;
    W.rot[i] = rand(0, Math.PI * 2); W.spin[i] = rand(-0.35, 0.35);
    W.vx[i] = rand(-0.07, 0.07) * HH; W.vy[i] = WHORL_RISE * 2 * HH * rand(0.8, 1.2);
  }

  _advanceWhorls(dt) {
    const W = this._w, n = this._whorlN;
    const HH = this._HH;
    this._whorlAcc += dt * WHORL_RATE;
    while (this._whorlAcc >= 1) {
      this._whorlAcc -= 1;
      this._spawnWhorl(this._tearX + rand(-0.03, 0.03) * HH, this._tearY);
    }
    const pos = this._wPos.array, sr = this._wSizeRot.array, sa = this._wStretchAlpha.array;
    const c = this._curlTmp;
    const t = this._time * 0.05;
    for (let i = 0; i < n; i++) {
      if (W.age[i] < 0) { sa[i * 2 + 1] = 0; continue; }
      W.age[i] += dt;
      const u = W.age[i] / W.life[i];
      if (u >= 1) { W.age[i] = -1; sa[i * 2 + 1] = 0; continue; }
      curl(W.x[i] / (CURL_SCALE * 2 * HH) + t, W.y[i] / (CURL_SCALE * 2 * HH) - t * 0.7, c);
      const cs = CURL_SPEED * 2 * HH;
      const tx = c[0] * cs, ty = c[1] * cs * 0.6 + W.vy[i] * (1 - 0.25 * u);
      W.vx[i] += (tx - W.vx[i]) * Math.min(1, dt / 1.5);
      W.vy[i] += (ty - W.vy[i]) * Math.min(1, dt / 1.5);
      W.x[i] += W.vx[i] * dt; W.y[i] += W.vy[i] * dt;
      W.rot[i] += W.spin[i] * dt * (1 - 0.5 * u);
      pos[i * 2] = W.x[i]; pos[i * 2 + 1] = W.y[i];
      sr[i * 2] = W.size[i] * (1 + (WHORL_GROW - 1) * u);
      sr[i * 2 + 1] = W.rot[i];
      sa[i * 2] = 1 + 0.9 * u;
      const birth = Math.min(1, W.age[i] / 0.6);
      sa[i * 2 + 1] = WHORL_ALPHA * birth * Math.pow(1 - u, 1.3);
    }
    this._wPos.needsUpdate = true; this._wSizeRot.needsUpdate = true; this._wStretchAlpha.needsUpdate = true;
  }

  // The fat lines need the drawing buffer's size: read it from the renderer
  // on the ribbon's own onBeforeRender (a plain hook on Mesh, no render inside).
  _onFrame(renderer) {
    renderer.getDrawingBufferSize(_size);
    this._ribbon.mat.resolution.set(_size.x, _size.y);
    this._twin.mat.resolution.set(_size.x, _size.y);
    if (this._tipMarks) this._tipMarks.mat.resolution.set(_size.x, _size.y);
    if (this._dustMat) this._dustMat.uniforms.uPx.value = Math.max(1.5, 2.2 * _size.y / 720);
  }

  // --- for the tests --------------------------------------------------------------
  get transitionHeight() { return this._transition; }
  get energy() { return this._energy; }
  get ribbon() { return { x: this._ribbon.x, y: this._ribbon.y, tear: [this._tearX, this._tearY], origin: [this._emberW.x, this._emberW.y] }; }
  get twin() { return { x: this._twin.x, y: this._twin.y }; }
  get whorls() { return this._w; }
  get ashState() { return { burn: this._burn, length: this._ashLen, period: this._ashPeriod, falling: this._ashFalling >= 0, falls: this._ashFalls, strip: this._ashPos.array, visible: this._ashMesh.visible }; }
  get stickMask() { return { base: this._baseW, axis: this._axis, halfX: this._stickHalfX, offX: this._stickOffX, top: this._stickTopW, ember: this._emberW }; }
  get emberTemp() { return this._emberTemp; }
  get emberPos() { return this._emberW; }
  get placement() { return { tip: this._tipW, base: this._baseW, axis: this._axis, angle: this._meta.angle, stickLen: this._stickLen }; }
  get hasPhoto() { return this._hasPhoto; }
  get frame() { return { HH: this._HH, HW: this._HW, tip: this._tipW, ray: { from: this._rayFrom, dir: this._rayDir, half: this._shared.uRayHalf.value } }; }
  revealAt(x, y) { return this._revealAt(x, y); }
  imgToWorld(u, v) { return this._imgToWorld(u, v, new THREE.Vector2()); }

  dispose() {
    const scene = this._scene;
    if (!scene) return;
    for (const m of this._objects) {
      scene.remove(m);
      if (m.geometry) m.geometry.dispose();
      if (m.material) m.material.dispose();
    }
    while (this._fallback.children.length) { const c = this._fallback.children.pop(); c.geometry.dispose(); c.material.dispose(); }
    scene.remove(this._fallback);
    if (this._photoTex) this._photoTex.dispose();
    if (this._nostickTex) this._nostickTex.dispose();
    this._objects = [];
    this._ribbon = this._twin = this._whorls = this._dust = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
